import { useLayoutEffect, useState, type RefObject } from "react";

/** Position approved menus and calendars in the viewport without changing the
 * layout of the form that opened them. The control still owns selection and
 * keyboard behavior. Excluded screens keep their original placement.
 */
export function useApprovedPopover(
  open: boolean,
  anchor: RefObject<HTMLElement>,
  popup: RefObject<HTMLElement>,
  kind: "select" | "date",
  optionCount = 0,
) {
  const [layout, setLayout] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight?: number;
    reserve: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (!open || document.documentElement.dataset.ui !== "approved-v5") {
      setLayout(null);
      return;
    }
    const compute = () => {
      const control = anchor.current;
      if (!control) return;
      const r = control.getBoundingClientRect();
      const viewport = window.visualViewport;
      const x = viewport?.offsetLeft ?? 0,
        y = viewport?.offsetTop ?? 0;
      const vw = viewport?.width ?? innerWidth,
        vh = viewport?.height ?? innerHeight;
      const desired = Math.min(
        popup.current?.scrollHeight ||
          (kind === "date" ? 330 : optionCount * 44 + 12),
        kind === "date" ? 380 : 280,
      );
      const nextReserve = 0;
      const leftBound = x + 8;
      const rightBound = x + vw - 8;
      const width = Math.min(
        kind === "date" ? 290 : Math.max(r.width, 180),
        rightBound - leftBound,
      );
      const topBound = y + 8;
      const bottomBound = y + vh - 14;
      const below = bottomBound - r.bottom - 6,
        above = r.top - topBound - 6;
      const flip = below < desired && above > below;
      const available = flip
        ? above
        : below;
      // Leave the list unconstrained when every option fits. A max-height
      // rounded a fraction below scrollHeight makes browsers paint a useless
      // scrollbar on short menus (for example the four game formats).
      const maxHeight =
        available + 2 >= desired
          ? undefined
          : Math.max(36, Math.min(desired, available));
      const top = flip
        ? Math.max(topBound, r.top - (maxHeight ?? desired) - 6)
        : r.bottom + 6;
      const next = {
        top,
        left: Math.max(
          leftBound,
          Math.min(
            r.left,
            rightBound - width,
          ),
        ),
        width,
        maxHeight,
        reserve: nextReserve,
      };
      setLayout((previous) =>
        previous &&
        Object.keys(next).every(
          (key) =>
            previous[key as keyof typeof next] ===
            next[key as keyof typeof next],
        )
          ? previous
          : next,
      );
    };
    compute();
    const frame = requestAnimationFrame(compute);
    const observer = new ResizeObserver(compute);
    if (anchor.current) observer.observe(anchor.current);
    const panel = anchor.current?.closest<HTMLElement>(
      '[data-testid="coach-modal-panel"], [data-testid="match-edit-modal"] > form, [data-confirm-panel]',
    );
    if (panel) observer.observe(panel);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    window.visualViewport?.addEventListener("resize", compute);
    window.visualViewport?.addEventListener("scroll", compute);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
      window.visualViewport?.removeEventListener("resize", compute);
      window.visualViewport?.removeEventListener("scroll", compute);
    };
  }, [open, anchor, popup, kind, optionCount]);
  return layout;
}
